/** The evidence endpoints, from the browser's side.
 *
 * Every amount crosses as a **string** (`api/schemas.py`), and the generated types say so, which is
 * why nothing here parses a figure into a number. A confidence is compared as a number because it is
 * a fraction and not a quantity — rounding a confidence changes a flag, not a filed figure.
 *
 * The one thing worth stating: after a verify, the inventory has changed. So `useVerify` invalidates
 * the whole query cache rather than a chosen key. Guessing which keys a new calculation affects is
 * exactly the failure ADR-0027 names — a stale figure sitting next to a fresh one — and a verify
 * happens rarely enough that refetching everything costs nothing.
 */

import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useCallback } from "react";

import { fileSize } from "../lib/format";
import { api } from "./client";
import { uploadDocument } from "./directUpload";
import { DiffRefused } from "./queries";
import type { ColumnMap, ColumnsNeedMapping } from "./queries";
import { refusalFrom } from "./refusal";
import type { components } from "./schema";

/** The closed set of document states, taken **from the generated schema** rather than restated.
 *
 * I first typed this as `string` and `tsc` refused it. That is exactly what ADR-0027 pays for: a
 * state the API does not have is a compile error, not a filter that silently matches nothing.
 */
export type DocumentState = components["schemas"]["DocumentState"];

export const evidenceKeys = {
  documents: (state?: DocumentState) => ["evidence", "documents", state ?? "all"] as const,
  document: (id: string) => ["evidence", "documents", id] as const,
  review: (id: string) => ["evidence", "documents", id, "review"] as const,
  limits: () => ["evidence", "limits"] as const,
};

/** Shapes, **aliased from the generated schema** rather than restated.
 *
 * They were hand-written first, and two of them were subtly wrong — `redacted_items` is optional in
 * the response and I had declared it required, which `tsc` caught the moment the assertions came off.
 * That is the whole argument for ADR-0027: a restated type is a second source of truth, and the one
 * that drifts is the one nobody regenerates.
 */
export type DocumentRow = components["schemas"]["DocumentOut"];
export type Overlap = components["schemas"]["OverlapOut"];
export type ReviewItem = components["schemas"]["ReviewItemOut"];
export type Review = components["schemas"]["ReviewOut"];
export type Verified = components["schemas"]["VerifiedOut"];
export type FacilityRow = components["schemas"]["FacilityOut"];
export type Usage = components["schemas"]["UsageOut"];
export type MeterChoice = components["schemas"]["MeterChoiceOut"];
/** The meters worth asking about for a bill under review, plus the saved reference (ADR-0107). */
export type MeterChoiceOffer = components["schemas"]["DocumentMeterChoicesOut"];
/** A group of an imported file that has a meter worth asking about (ADR-0099). */
export type ImportMeterGroup = components["schemas"]["ImportMeterGroupOut"];
/** A person's confirmation that one group's rows came off one meter (ADR-0099). */
export type ImportMeterChoice = components["schemas"]["ImportMeterChoiceIn"];
/** A rejection, **as an envelope** — `already_rejected` is the field, and it is the whole reason
 * `useReject` returns anything at all (EXP-35). */
export type Rejected = components["schemas"]["RejectedOut"];
/** One extraction run. `already_extracted` says this request did not run it, and
 * `redacted_items` is `null` on that path because nothing was read, redacted or written. */
export type Extracted = components["schemas"]["ExtractionOut"];
/** What is left of an interrupted intake, and **why nothing is left when nothing is**. */
export type Intake = components["schemas"]["IntakeOut"];
export type Waiting = components["schemas"]["WaitingOut"];

/** Documents, and **how many there are** (DOC-06).
 *
 * `total` rather than just the rows, because a list that stops at its limit is a list the reader
 * believes is complete — and this one is read by the screen whose job is to say what is
 * outstanding. `X-Total-Count` is the server's own count after the same filter.
 *
 * `oldestFirst` because newest-first drops exactly the wrong end: the document nobody has looked
 * at for three weeks is the one worth finding, and it was the first to fall off the page.
 */
export function useDocuments(
  state?: DocumentState,
  options?: { oldestFirst?: boolean; limit?: number },
) {
  const oldestFirst = options?.oldestFirst ?? false;
  const limit = options?.limit;
  return useQuery({
    // The limit is part of the key: a read of 50 and a read of 200 are different answers, and
    // sharing a cache slot would show the first while the second was in flight.
    queryKey: [...evidenceKeys.documents(state), oldestFirst, limit ?? "default"] as const,
    // Raising the ceiling changes the key, and without this the whole screen went back to
    // "Loading…" and dropped the queue while the larger read ran. The rows already held are still
    // true; they are shown until the longer list replaces them.
    placeholderData: keepPreviousData,
    queryFn: () => readDocuments({ state, oldestFirst, limit }),
  });
}

/** What the library can be asked, **answered by the database** (gate 18).
 *
 * Every field is optional and an absent field asks nothing. The server owns what each means
 * (`backend/app/modules/evidence/library.py`): a search is every word against the stored
 * vocabulary, a period is an overlap, and `figures` counts figures in force. Nothing here
 * re-implements any of it, because a second matcher on this side is how a search and a card come to
 * disagree.
 */
export interface LibraryFilters {
  /** Free text. An empty or blank string asks nothing. */
  q?: string;
  kind?: components["schemas"]["DocumentKind"];
  /** ISO dates, inclusive. A document overlapping the range is returned. */
  periodFrom?: string;
  periodTo?: string;
  /** Documents with no established period. Contradicts a range, and the server refuses both. */
  periodUnknown?: boolean;
  figures?: "linked" | "unlinked";
  /** A lifecycle state: the Documents place's Stage filter (ADR-0212). */
  state?: DocumentState;
  /** A site id, or `none` (the server's own bucket for a document with neither a hint nor a figure
   * there). The site the uploader named or a figure in force is at - never one a bill names. */
  facilityId?: string;
}

/** Whether a filter asks anything - the one place that decides whether the library is read whole or
 * read as an answer to a question. */
export function isFiltered(filters: LibraryFilters): boolean {
  return Boolean(
    filters.q?.trim() ||
      filters.kind ||
      filters.periodFrom ||
      filters.periodTo ||
      filters.periodUnknown ||
      filters.figures ||
      filters.state ||
      filters.facilityId,
  );
}

async function readDocuments(request: {
  state?: DocumentState;
  oldestFirst: boolean;
  limit?: number;
  offset?: number;
  filters?: LibraryFilters;
}): Promise<{ rows: DocumentRow[]; total: number; reportedTotal: number | null }> {
  const filters = request.filters ?? {};
  const search = filters.q?.trim();
  const { data, error, response } = await api.GET("/api/v1/evidence/", {
    params: {
      query: {
        ...(request.state || filters.state ? { state: request.state ?? filters.state } : {}),
        ...(request.oldestFirst ? { oldest_first: true } : {}),
        ...(request.limit ? { limit: request.limit } : {}),
        ...(request.offset ? { offset: request.offset } : {}),
        ...(search ? { q: search } : {}),
        ...(filters.kind ? { kind: filters.kind } : {}),
        ...(filters.periodFrom ? { period_from: filters.periodFrom } : {}),
        ...(filters.periodTo ? { period_to: filters.periodTo } : {}),
        ...(filters.periodUnknown ? { period_unknown: true } : {}),
        ...(filters.figures ? { figures: filters.figures } : {}),
        ...(filters.facilityId ? { facility_id: filters.facilityId } : {}),
      },
    },
  });
  // **Thrown, not defaulted.** This was `const rows = data ?? []`, so a 500 - or a dropped
  // request - made the query *succeed* with an empty library, and the Evidence screen printed
  // "Nothing is waiting for a person." and "Nothing has been refused." on a read that never
  // happened. That is the most repeated defect in this codebase (EXP-29); a failed read has to
  // reach `Loaded` as a failure or no screen can tell it from an empty tenant.
  if (error || !data) {
    throw refusalFrom(response, error, "The evidence library could not be read.");
  }
  const rows = data;
  // Falls back to the page's own length rather than to zero: a proxy that strips the header
  // should make the screen say nothing about a remainder, not claim there are none.
  const counted = Number(response.headers.get("x-total-count"));
  const header = response.headers.get("x-total-count");
  const reportedTotal = header !== null && /^\d+$/.test(header.trim()) && Number.isSafeInteger(Number(header))
    ? Number(header) : null;
  return { rows, total: Number.isFinite(counted) && counted > 0 ? counted : rows.length, reportedTotal };
}

/** How many documents the library asks for first, and then at a time. **These are this screen's own
 * requests and not claims about the server**: both are sent, so nothing here depends on what the
 * API's default happens to be. The second is the API's maximum (`maximum: 200` in the generated
 * schema) — a mirror, and a mirror and not an authority: a larger one is refused as a failed read
 * rather than quietly cut. */
export const LIBRARY_FIRST_PAGE = 50;
export const LIBRARY_NEXT_PAGE = 200;

/** The whole evidence library, **a page at a time, newest first** — gate 18, and G-Wviewer-24.
 *
 * `useDocuments` could be raised to the API's ceiling and no further, so a workspace holding more
 * than 200 documents had some that no client could ever list. This reads the same endpoint with an
 * `offset`, and the pages accumulate: `data.rows` is every page read so far and `data.total` is the
 * server's own count after the last one, so the screen can say how many it is *not* showing and
 * ask for the next page.
 *
 * Three decisions, each of which is a way a paged list lies:
 *
 * - **Newest first, always.** This is the register of everything held, and a person opening it
 *   looks for what has just arrived (a 302-document library that opened on its oldest meter-export
 *   CSVs was the gap). The document that has waited longest is DOC-06's concern and is the
 *   Processing queue's, and this page's "Needs review" view. An upload while a page is being read
 *   shifts every later offset by one, which **re-reads a row already held** (dropped by id below)
 *   and never skips one: a library only grows at the front, so nothing slides past the cut.
 * - **The next offset is the sum of the limits asked for, not the rows that came back.** The API
 *   filters an auditor's page *after* it is cut, so a page can be shorter than its limit; advancing
 *   by `rows.length` would read the same rows again.
 * - **A repeated row is dropped by id.** The offsets make that unreachable, and a list that shows
 *   one document twice is a list that doubles a figure somebody counts from it.
 */
export function useDocumentLibrary() {
  return useInfiniteQuery({
    queryKey: [...evidenceKeys.documents(), "library"] as const,
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {
      const limit = pageParam === 0 ? LIBRARY_FIRST_PAGE : LIBRARY_NEXT_PAGE;
      const page = await readDocuments({ oldestFirst: false, limit, offset: pageParam });
      return { ...page, consumed: pageParam + limit };
    },
    getNextPageParam: (last) => (last.consumed < last.total ? last.consumed : undefined),
    select: (pages): { rows: DocumentRow[]; total: number; reportedTotal: number | null } => {
      const seen = new Set<string>();
      const rows: DocumentRow[] = [];
      for (const page of pages.pages) {
        for (const row of page.rows) {
          if (seen.has(row.id)) continue;
          seen.add(row.id);
          rows.push(row);
        }
      }
      return { rows, total: pages.pages.at(-1)?.total ?? rows.length, reportedTotal: pages.pages.at(-1)?.reportedTotal ?? null };
    },
  });
}

/** The library **as the answer to a question** - gate 18's search, run by the database.
 *
 * `useDocumentLibrary` reads the whole library a page at a time, and a search over the pages it
 * holds cannot find a document it has not paged to. This asks the server instead, with the same
 * newest-first order and the same offset paging, so the first page of an answer and the 201st are
 * equally reachable. It is **disabled for an empty question**: the unfiltered library is the other
 * hook's read, and a second copy of it under a different key would be two reads of one thing.
 *
 * `data.total` is the server's own count *of this answer* (the header is taken after the same
 * filters as the rows), so "Showing 3 of 41 matching" is a fact about the question and never about
 * the library. A change of question changes the key and the previous answer stays on screen until
 * the new one arrives - a search box that blanked the list on every keystroke would read as the
 * library emptying.
 */
export function useDocumentSearch(filters: LibraryFilters) {
  return useInfiniteQuery({
    queryKey: [...evidenceKeys.documents(), "library-search", filters] as const,
    enabled: isFiltered(filters),
    initialPageParam: 0,
    placeholderData: keepPreviousData,
    queryFn: async ({ pageParam }) => {
      const limit = pageParam === 0 ? LIBRARY_FIRST_PAGE : LIBRARY_NEXT_PAGE;
      const page = await readDocuments({ oldestFirst: false, limit, offset: pageParam, filters });
      return { ...page, consumed: pageParam + limit };
    },
    getNextPageParam: (last) => (last.consumed < last.total ? last.consumed : undefined),
    select: (pages): { rows: DocumentRow[]; total: number; reportedTotal: number | null } => {
      const seen = new Set<string>();
      const rows: DocumentRow[] = [];
      for (const page of pages.pages) {
        for (const row of page.rows) {
          if (seen.has(row.id)) continue;
          seen.add(row.id);
          rows.push(row);
        }
      }
      return {
        rows,
        total: pages.pages.at(-1)?.total ?? rows.length,
        reportedTotal: pages.pages.at(-1)?.reportedTotal ?? null,
      };
    },
  });
}

/** What is left of an interrupted intake — **EXP-35**, and the route no screen was asking.
 *
 * `GET /evidence/resume` groups this tenant's unfinished documents by what would move each on, and
 * counts them in the database rather than in a page of rows. It was built, tested and documented
 * and nothing in the browser had ever called it, so *"resume intake without losing decisions"* was
 * a server-side fact with no reader.
 *
 * **`verdict` is the field to read, never `waiting.length`.** `nothing_uploaded` and
 * `everything_decided` both come back with nothing waiting and only the second one means *done* —
 * rendering emptiness as success is F07's `any([])` in a different language.
 *
 * **Thrown, not defaulted.** `return data ?? []`-shaped code is what made nine read hooks succeed
 * with nothing in them; here it would make a dropped request render as *"nothing outstanding"*,
 * which is the single most confident sentence this panel can produce.
 */
export function useIntake(enabled = true) {
  return useQuery({
    queryKey: ["evidence", "resume"] as const,
    enabled,
    queryFn: async (): Promise<Intake> => {
      const { data, error, response } = await api.GET("/api/v1/evidence/resume", {});
      if (error || !data) {
        throw refusalFrom(response, error, "What is outstanding could not be read.");
      }
      return data;
    },
  });
}

export function useReview(documentId: string | undefined) {
  return useQuery({
    queryKey: evidenceKeys.review(documentId ?? "none"),
    enabled: Boolean(documentId),
    queryFn: async (): Promise<Review | null> => {
      if (!documentId) return null;
      const { data, error, response } = await api.GET("/api/v1/evidence/{document_id}/review", {
        params: { path: { document_id: documentId } },
      });
      // 404 means "not extracted yet", which is a state to render and not a failure.
      if (response.status === 404) return null;
      // **Everything else is a failure, and it has to be thrown.** `return data ?? null` swallowed
      // it: the query *succeeded* with `null`, so `Loaded` could never see a failed read and the
      // screen rendered "this document has not been read yet" for a 500 (EXP-29). The `data = []`
      // defect, one level down, where no screen could repair it.
      if (error) throw refusalFrom(response, error, "This extraction could not be read.");
      return data ?? null;
    },
    retry: false,
  });
}

export type UploadLimit = components["schemas"]["UploadLimitOut"];

/** The largest file the server accepts, **read from the server and held nowhere else** (ADR-0058).
 *
 * The SPA has no copy of this number, and that is the point of the read. `Evidence.tsx` printed
 * "up to 25 MB" for as long as the deployed ceiling was 4.5 MiB, which is what a second home for a
 * number does: it goes on saying the old one. The deployment sets the limit
 * (`EMITIQ_MAX_EVIDENCE_BYTES`, `infra/platform/api.tf`) and this reads it back.
 *
 * Ten minutes of freshness, not none: it changes when the server is redeployed and at no other
 * time, and a screen that re-read it on every focus would be asking a question with one answer.
 */
export const uploadLimitQuery = {
  queryKey: evidenceKeys.limits(),
  queryFn: async (): Promise<UploadLimit> => {
    const { data, error, response } = await api.GET("/api/v1/evidence/limits", {});
    // Thrown, not defaulted: `data ?? someNumber` is a second home for the number wearing a
    // fallback's clothes, and it would be wrong in exactly the case it exists for.
    if (error || !data) throw refusalFrom(response, error, "The file-size limit could not be read.");
    return data;
  },
  staleTime: 10 * 60_000,
  retry: false,
} as const;

export function useUploadLimit() {
  return useQuery(uploadLimitQuery);
}

/** Why a file is too large to send, or `null` when it is not: *"report.pdf is 7.3 MB, and the
 * limit is 4 MB."*
 *
 * **Pure, and the server's own sentence.** The number comes from `limit.max_bytes`, the second
 * half of the sentence is `limit.message` — written once in `validation.limit_message`, so this
 * refusal and the server's read identically (design principle 7). What this function adds is the
 * one fact only the browser has: the file's size.
 *
 * It returns the lead only. What happened and what to do next differ by screen (an upload sends
 * nothing; a workbook comparison compares nothing), so each caller appends its own consequence —
 * and the evidence path appends `limit.remedy`, the server's remedy for exactly this refusal.
 */
export function oversizeLead(file: File, limit: UploadLimit): string | null {
  if (file.size <= limit.max_bytes) return null;
  return `${file.name} is ${fileSize(file.size)}, and ${limit.message}.`;
}

/** A refusal made in the browser, before any request. */
export interface OversizeRefusal {
  /** `oversizeLead`'s sentence. */
  lead: string;
  /** The server's remedy for a file that is too large. Evidence-shaped (it talks about scans and
   * statements), so a caller whose file is something else says its own. */
  remedy: string;
}

/** Whether a file may be sent at all — **the only check that can say anything above 10 MiB.**
 *
 * The platform refuses a body over its cap before the application runs (ADR-0058). From 4.5 MiB
 * to 10 MiB that refusal is a 413 a browser can read, though not in the server's shape; **from
 * 10 MiB it carries no CORS headers, so `fetch` rejects with no status and no message** — which is
 * indistinguishable from the API being down, and is what a customer with a phone photograph or a
 * large spreadsheet would meet. No response exists to render. This runs *before* the request, so
 * it is the one place a message can be produced for that file, and it must therefore not depend on
 * anything that could be missing at that moment.
 *
 * **The limit is fetched here rather than assumed loaded.** A hook-supplied value would be
 * `undefined` for the first moments of a screen, and an upload started in that window would skip
 * the check and meet the very failure it exists for. `fetchQuery` answers from the cache when it
 * is fresh.
 *
 * **If the limit cannot be read, the file is sent** (`null`). The server still enforces its limit
 * for everything under the platform's cap; refusing every upload because a read failed would turn
 * a missing sentence into a missing product. What is lost is only the message for a file over
 * 10 MiB, and the screens say the limit could not be read.
 */
export function useUploadPreCheck() {
  const queryClient = useQueryClient();
  return useCallback(
    async (file: File): Promise<OversizeRefusal | null> => {
      let limit: UploadLimit;
      try {
        limit = await queryClient.fetchQuery(uploadLimitQuery);
      } catch {
        return null;
      }
      const lead = oversizeLead(file, limit);
      return lead === null ? null : { lead, remedy: limit.remedy };
    },
    [queryClient],
  );
}

export { DuplicateUploadError, UploadInterrupted, uploadDocument } from "./directUpload";
export type { UploadProgress, Uploaded } from "./directUpload";

/** Send a document: declare it, put the bytes at the landing bucket, confirm (ADR-0058, A2).
 *
 * `directUpload.ts` is the flow and its comment is the argument; this is the mutation around it.
 * The three failures a caller must tell apart are `Refused` (the server said no — do not retry),
 * `DuplicateUploadError` (look at what is already held) and `UploadInterrupted` (the network or the
 * platform interrupted — and `stage` says whether a retry sends again or only confirms again).
 * A confirmed upload comes back with `accepted: false` when the document was quarantined.
 */
export function useUpload() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: uploadDocument,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["evidence"] });
    },
  });
}

export function useExtract() {
  const queryClient = useQueryClient();
  return useMutation({
    //: **No body.** The route reads the stored object (ADR-0028), so there is nothing to send but
    //: the document's id — and the digest on that document now covers the text its figures came
    //: from, which it did not when the text arrived in this request.
    //:
    //: **The extraction comes back rather than being discarded** (EXP-35). Two facts on it are
    //: the caller's to render and were being thrown away at this boundary: `already_extracted`,
    //: which says this request did not read anything because an earlier one already had, and
    //: `redacted_items`, which is `null` on exactly that path — nobody asked, as opposed to asked
    //: and found nothing.
    mutationFn: async (documentId: string): Promise<Extracted> => {
      const { data, error, response } = await api.POST("/api/v1/evidence/{document_id}/extract", {
        params: { path: { document_id: documentId } },
      });
      if (error || !data) throw refusalFrom(response, error, "That document could not be read.");
      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["evidence"] });
    },
  });
}

/** Accept an extraction and record the figure.
 *
 * Three of the four body fields are the reviewer's answer to something the server asked:
 *
 * - `expectedVersion` — the version they opened, so a decision made against a document someone else
 *   has since changed is refused rather than applied (EXP-34).
 * - `supersedeActivityRecordId` — "this corrects that figure", after a 409 `duplicate_activity`.
 * - `allowDuplicate` — "these are two separate consumptions", the other answer to the same 409.
 * - `fuelUse` — for a fuel invoice, whether it was burned in a vehicle or in fixed equipment. Most
 *   invoices do not say, the two are different categories with different factors, and there is no
 *   default (EXP-30). Sent as `null` when the document is not a fuel invoice, where it is ignored.
 *
 * `allow_duplicate` is sent explicitly as `false` rather than omitted. It has a server-side default,
 * and relying on it would mean the request that doubles a figure is the one that says nothing.
 */
export function useVerify() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      documentId: string;
      facilityId: string;
      expectedVersion?: number;
      supersedeActivityRecordId?: string;
      allowDuplicate?: boolean;
      fuelUse?: "mobile" | "stationary";
      /** This site's share, when the statement covers several (DOC-04). Omitted means all of it. */
      quantity?: string;
      /** The supply point a person chose for this bill (ADR-0098). Omitted records none. */
      meterId?: string;
      /** The reviewer ticked *remember* after choosing `meterId` (ADR-0107). The reference itself
       * is read by the server from the extraction; it is never typed or sent from here. */
      rememberMeterReference?: boolean;
      /** An Admin's reason, sent only on the second attempt after `period_closed` (ADR-0094). */
      postCloseReason?: string;
    }): Promise<Verified> => {
      const { data, error, response } = await api.POST("/api/v1/evidence/{document_id}/verify", {
        params: { path: { document_id: input.documentId } },
        body: {
          facility_id: input.facilityId,
          expected_version: input.expectedVersion ?? null,
          supersede_activity_record_id: input.supersedeActivityRecordId ?? null,
          allow_duplicate: input.allowDuplicate ?? false,
          fuel_use: input.fuelUse ?? null,
          quantity: input.quantity ?? null,
          meter_id: input.meterId ?? null,
          // Always a boolean (the schema requires one), and true only with a meter the person chose.
          remember_meter_reference: Boolean(input.rememberMeterReference && input.meterId),
          ...(input.postCloseReason ? { post_close_reason: input.postCloseReason } : {}),
        },
      });
      if (error || !data) {
        // The duplicate stays its own class — it is a **question** with two answers, not a failure.
        // Everything else keeps its status and its code, so the screen can tell a 403 from the
        // `stale_version` this route exists to be able to refuse (EXP-34).
        throw (
          duplicateOf(error) ??
          refusalFrom(response, error, "That figure could not be recorded.")
        );
      }
      return data;
    },
    onSuccess: () => {
      // Everything. A verify adds a calculation, so which figures are stale is not a question worth
      // answering by hand — and a wrong answer shows a stale total beside a fresh one.
      queryClient.clear();
    },
  });
}

/** Record another site's share of a document already verified (DOC-04).
 *
 * A separate mutation rather than a second call to `useVerify`, because it is a different act: the
 * document is already verified and nothing about its state changes. What changes is how much of the
 * statement has been attributed to somewhere.
 *
 * Refusals come back through the same two channels as a verification's — a `DuplicateActivity` when
 * the figure lands on one already recorded, and a plain message otherwise, which for this route is
 * usually "the sites together would claim more than the statement evidences".
 */
export function useAddActivity() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      documentId: string;
      facilityId: string;
      quantity?: string;
      expectedVersion?: number;
      supersedeActivityRecordId?: string;
      allowDuplicate?: boolean;
      fuelUse?: "mobile" | "stationary";
      /** The supply point a person chose for this site's share (ADR-0098). */
      meterId?: string;
      /** An Admin's reason, sent only on the second attempt after `period_closed` (ADR-0094). */
      postCloseReason?: string;
    }): Promise<Verified> => {
      const { data, error, response } = await api.POST("/api/v1/evidence/{document_id}/activities", {
        params: { path: { document_id: input.documentId } },
        body: {
          facility_id: input.facilityId,
          quantity: input.quantity ?? null,
          expected_version: input.expectedVersion ?? null,
          supersede_activity_record_id: input.supersedeActivityRecordId ?? null,
          allow_duplicate: input.allowDuplicate ?? false,
          fuel_use: input.fuelUse ?? null,
          meter_id: input.meterId ?? null,
          ...(input.postCloseReason ? { post_close_reason: input.postCloseReason } : {}),
        },
      });
      if (error || !data) {
        throw (
          duplicateOf(error) ??
          refusalFrom(response, error, "That site's share could not be recorded.")
        );
      }
      return data;
    },
    onSuccess: () => {
      // Same reasoning as `useVerify`: a new figure makes every total stale, and guessing which
      // ones shows a fresh number beside an old one.
      queryClient.clear();
    },
  });
}

/** The meters a person could say this bill was for, at the site they have chosen (ADR-0098).
 *
 * Empty for most sites — only a meter with a stream declared for it is listed — and then the
 * screen shows no control. `enabled` on a chosen site: with none there is nothing to ask. The
 * choice is optional, so a failed read costs only the picker: the caller shows none, and the gap
 * stays visible, which is the honest outcome.
 */
export function useMeterChoiceOffer(
  documentId: string | undefined,
  facilityId: string,
  fuelUse: "mobile" | "stationary" | undefined,
  enabled: boolean,
) {
  return useQuery({
    queryKey: [
      "evidence",
      "documents",
      documentId ?? "none",
      "meter-choices",
      facilityId,
      fuelUse ?? "any",
    ] as const,
    enabled: Boolean(documentId) && facilityId !== "" && enabled,
    retry: false,
    queryFn: async (): Promise<MeterChoiceOffer> => {
      if (!documentId) return { meters: [], rememberable: false };
      const { data, error, response } = await api.GET(
        "/api/v1/evidence/{document_id}/meter-choices",
        {
          params: {
            path: { document_id: documentId },
            query: { facility_id: facilityId, ...(fuelUse ? { fuel_use: fuelUse } : {}) },
          },
        },
      );
      if (error) throw refusalFrom(response, error, "This site's meters could not be read.");
      return data ?? { meters: [], rememberable: false };
    },
  });
}

/** Just the list of meters, for a caller that does not need the saved reference. */
export function useMeterChoices(
  documentId: string | undefined,
  facilityId: string,
  fuelUse: "mobile" | "stationary" | undefined,
  enabled: boolean,
) {
  const offer = useMeterChoiceOffer(documentId, facilityId, fuelUse, enabled);
  return { ...offer, data: offer.data?.meters };
}

/** What a document became, and which filings would state it (EXP-19).
 *
 * The direction that did not exist. A receipt has always reached from a figure to a document id;
 * nothing reached back, so *"and where does this bill end up?"* had no answer in the product.
 *
 * `enabled` on the document being verified: an unverified one has produced nothing, and asking
 * would be a request whose answer is always two empty lists.
 */
export function useUsage(documentId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ["evidence", "documents", documentId ?? "none", "usage"] as const,
    enabled: Boolean(documentId) && enabled,
    queryFn: async (): Promise<Usage | null> => {
      if (!documentId) return null;
      const { data, error, response } = await api.GET("/api/v1/evidence/{document_id}/usage", {
        params: { path: { document_id: documentId } },
      });
      if (error) throw refusalFrom(response, error, "What this document became could not be read.");
      return data ?? null;
    },
  });
}

/** Reject a document, and say which version is being rejected (EXP-34).
 *
 * `expectedVersion` for the same reason `useVerify` carries one: a rejection decided against an
 * extraction somebody has since corrected is a decision about a document that no longer exists,
 * and it is the refusal direction nobody notices — a *rejection* applied by mistake looks like
 * nothing having happened rather than like a wrong figure.
 */
export function useReject() {
  const queryClient = useQueryClient();
  return useMutation({
    //: **The answer is read, not discarded** (EXP-35). This returned `void`: the route has
    //: answered `already_rejected` since the backend half shipped, and the one client that calls
    //: it destructured `{ error, response }` and dropped `data` — so a second rejecter was
    //: navigated away exactly as though theirs were the rejection that landed. It was not: the
    //: state is terminal, **their reason was never recorded**, and the reason on the document is
    //: the first rejecter's. A screen cannot say so from a `void`.
    mutationFn: async (input: {
      documentId: string;
      reason: string;
      expectedVersion?: number;
    }): Promise<Rejected> => {
      const { data, error, response } = await api.POST("/api/v1/evidence/{document_id}/reject", {
        params: { path: { document_id: input.documentId } },
        body: { reason: input.reason, expected_version: input.expectedVersion ?? null },
      });
      if (error || !data) {
        throw refusalFrom(response, error, "That rejection could not be recorded.");
      }
      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["evidence"] });
    },
  });
}

/** Pull the message out of an API error body.
 *
 * Every error from these routes is `{detail: {error, message}}`, and `message` is written for the
 * person looking at the document — "the PDF contains JavaScript … a print-to-PDF copy will pass", not
 * a status code. Showing the code instead would waste the work of writing them.
 */
/** A verification the server refused because the figure overlaps one already recorded.
 *
 * A typed error rather than a string, because the reviewer has to *choose*, and a choice needs the
 * candidates. `messageOf` would reduce this to a sentence and the screen would have nothing to
 * render but an apology.
 */
export class DuplicateActivity extends Error {
  readonly overlaps: readonly Overlap[];

  constructor(message: string, overlaps: readonly Overlap[]) {
    super(message);
    this.name = "DuplicateActivity";
    this.overlaps = overlaps;
  }
}

function duplicateOf(error: unknown): DuplicateActivity | null {
  if (
    error &&
    typeof error === "object" &&
    "detail" in error &&
    error.detail &&
    typeof error.detail === "object" &&
    "error" in error.detail &&
    error.detail.error === "duplicate_activity" &&
    "overlaps" in error.detail &&
    Array.isArray(error.detail.overlaps)
  ) {
    return new DuplicateActivity(messageOf(error), error.detail.overlaps as Overlap[]);
  }
  return null;
}

function messageOf(error: unknown): string {
  if (
    error &&
    typeof error === "object" &&
    "detail" in error &&
    error.detail &&
    typeof error.detail === "object" &&
    "message" in error.detail &&
    typeof error.detail.message === "string"
  ) {
    // The remedy travels with the message wherever one was sent (DOC-01, EXP-05). A refusal a
    // person cannot act on produces a support conversation rather than a second attempt that
    // works, and every route on this API that refuses now offers a next step.
    const remedy =
      "remedy" in error.detail && typeof error.detail.remedy === "string"
        ? error.detail.remedy
        : "";
    return [error.detail.message, remedy].filter(Boolean).join(" ");
  }
  return "That could not be done.";
}


/** The organisation's sites.
 *
 * Its own query rather than derived from figures: a new workspace has sites and no calculations, and
 * an empty dropdown at the moment someone records their first figure would be the one time it
 * mattered. No `org_id` — the session decides which organisation this is.
 */
export function useFacilities(enabled = true) {
  return useQuery({
    queryKey: ["facilities"] as const,
    enabled,
    queryFn: async (): Promise<FacilityRow[]> => {
      const { data, error, response } = await api.GET("/api/v1/facilities", {});
      // **Thrown, not defaulted.** This hook is the site list behind four pickers, and
      // `return data ?? []` made a failed request indistinguishable from a workspace with no
      // sites — on the screens where choosing a site is the required act. The docstring above
      // already said an empty dropdown would be the one time it mattered.
      if (error) throw refusalFrom(response, error, "The list of sites could not be read.");
      return data ?? [];
    },
  });
}

export type ImportPreview = components["schemas"]["ImportPreviewOut"];
export type Imported = components["schemas"]["ImportedOut"];

/** What a CSV would produce, and everything wrong with it. Writes nothing.
 *
 * `enabled` on a document kind rather than unconditionally: asking for a preview of a PDF returns a
 * 422 explaining that it is not a CSV, which is a correct answer to a question nobody asked.
 */
export function useImportPreview(
  documentId: string | undefined,
  enabled: boolean,
  /** The mapping a person confirmed on this file's columns (gate 17). `null` reads it by its own
   * names. It is part of the cache key, so the file shown under one mapping is never served for
   * another. */
  columnMap: ColumnMap | null = null,
) {
  return useQuery({
    queryKey: [
      "evidence",
      "documents",
      documentId ?? "none",
      "import",
      columnMap ? JSON.stringify(columnMap) : "own-columns",
    ] as const,
    enabled: Boolean(documentId) && enabled,
    retry: false,
    queryFn: async (): Promise<ImportPreview | null> => {
      if (!documentId) return null;
      const { data, error, response } = await api.GET("/api/v1/evidence/{document_id}/import/preview", {
        params: {
          path: { document_id: documentId },
          query: columnMap ? { column_map: JSON.stringify(columnMap) } : {},
        },
      });
      if (error) {
        // **The code, not only the sentence.** A file whose columns are not ours is not a broken
        // file: the screen has a way forward, and it can only take it if the refusal says so.
        const needs = columnsNeedMapping(error);
        if (needs) {
          throw new DiffRefused(response.status, "columns_need_mapping", needs.message, needs.columns);
        }
        // A refusal by role keeps its code and the roles the server named, so the screen says who
        // can open an import and offers no retry (Q-05, G-P6-02).
        if (response.status === 403) throw refusalFrom(response, error, messageOf(error));
        throw new Error(messageOf(error));
      }
      return data ?? null;
    },
  });
}

/** The mapping step's input out of a refusal body, or `null` when the refusal is any other. Only
 * when the body carries the lists the step is built from: one that says so and brings no columns is
 * a sentence, not a step (`refusalDetail`'s rule for the round trip). */
function columnsNeedMapping(
  error: unknown,
): { message: string; columns: ColumnsNeedMapping } | null {
  const detail = (error as { detail?: unknown } | null)?.detail;
  if (!detail || typeof detail !== "object") return null;
  const body = detail as { error?: unknown; message?: unknown } & Partial<ColumnsNeedMapping>;
  if (
    body.error !== "columns_need_mapping" ||
    !Array.isArray(body.columns) ||
    !Array.isArray(body.targets)
  ) {
    return null;
  }
  return {
    message: typeof body.message === "string" ? body.message : "Say which column is which.",
    columns: {
      columns: body.columns,
      targets: body.targets,
      missing: body.missing ?? [],
      conflicts: body.conflicts ?? [],
    },
  };
}

/** Import a meter export's rows (EXP-35).
 *
 * **The retry is answered by the server, before the state machine** — a second call returns
 * `already_imported` with the counts the first one produced, rather than refusing an invalid
 * transition. That is what makes a timed-out request safe to send again, and it is why the screen
 * may say so.
 *
 * `expectedVersion` is the version the reviewer read the preview against. It is a query parameter
 * on this route rather than a body field, and it is optional here because
 * **`ImportPreviewOut` does not carry the document's version** — the screen has to read the
 * document separately to know it, and where it has not, no check is claimed.
 */
export function useAcceptImport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      documentId: string;
      expectedVersion?: number;
      /** An Admin's reason, sent only on the second attempt after `period_closed` (ADR-0094). */
      postCloseReason?: string;
      /** The meters a person confirmed for groups of the file (ADR-0099). Only the groups they
       * answered: no body at all when there are none, so an ordinary import's request is unchanged
       * and a group left blank records no meter. */
      meterChoices?: ImportMeterChoice[];
      /** The mapping the preview was read with - sent so the file is recorded exactly as it was
       * shown (gate 17). Absent: read by its own column names. */
      columnMap?: ColumnMap | null;
    }): Promise<Imported> => {
      const body = {
        ...(input.meterChoices?.length ? { meter_choices: input.meterChoices } : {}),
        ...(input.columnMap ? { column_map: input.columnMap } : {}),
      };
      const { data, error, response } = await api.POST("/api/v1/evidence/{document_id}/import", {
        params: {
          path: { document_id: input.documentId },
          query: {
            ...(input.expectedVersion ? { expected_version: input.expectedVersion } : {}),
            ...(input.postCloseReason ? { post_close_reason: input.postCloseReason } : {}),
          },
        },
        ...(Object.keys(body).length > 0 ? { body } : {}),
      });
      if (error || !data) {
        throw refusalFrom(response, error, "That file could not be imported.");
      }
      return data;
    },
    // Everything, as with a verify: an import adds figures across several periods, and guessing
    // which cached totals it moved is how a stale number ends up beside a fresh one.
    onSuccess: () => queryClient.clear(),
  });
}


/** Change one field a machine read wrongly, or did not read at all (F12).
 *
 * The response is the **whole review**, recomputed on the server. Patching the one field locally
 * would let a screen render a correction beside stale siblings — and the sibling that matters is
 * the period end, which is the field extraction most often misses.
 */
export function useCorrect(documentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      field: string;
      value: string;
      reason: string;
      /** **The correction this reviewer was looking at for this field, or `null`** — ADR-0054,
       * T-08. Required rather than optional, and the type says so: `null` is the assertion
       * *"nothing was in force when I read this"*, not an absence. An optional field is how
       * `expectedVersion` sat unsent from `Review.tsx` for weeks while the server's refusal was
       * built, tested and documented — a guard is not a guard until something calls it.
       *
       * It comes from `item.correction_id` in the review the server returned, never from anything
       * the client computed: the same rule the spreadsheet round trip follows with `revision_id`.
       */
      expectedCorrectionId: string | null;
    }): Promise<Review> => {
      const { data, error, response } = await api.POST("/api/v1/evidence/{document_id}/corrections", {
        params: { path: { document_id: documentId } },
        body: {
          field: input.field,
          value: input.value,
          reason: input.reason,
          expected_correction_id: input.expectedCorrectionId,
        },
      });
      if (error || !data) {
        throw refusalFrom(response, error, "That correction could not be saved.");
      }
      return data;
    },
    onSuccess: async (review) => {
      queryClient.setQueryData(evidenceKeys.review(documentId), review);
      // **And the source pane, which is a sibling query and was left stale.** This function's own
      // docstring argues that patching one field locally *"would let a screen render a correction
      // beside stale siblings"* — true, and the reasoning stopped at the review. `useDocumentText`
      // answers for every field too, and a corrected field must report `corrected` rather than
      // whatever the extraction recorded; without this it went on saying `not_recorded`.
      //
      // Found by `make ui-check`, and **not** by any of the 210 vitest tests, because those stub
      // the fetch and so cannot see a cache that was never asked again. It is the same shape as
      // `useReceipt`'s rule: a figure that looks live when it has been replaced is the one nobody
      // re-checks.
      await queryClient.invalidateQueries({
        queryKey: ["evidence", "documents", documentId, "text"],
      });
    },
  });
}

/** A short-lived URL for the stored bytes (ADR-0009: never bytes through the API).
 *
 * **Not cached.** The URL expires, so a cached one is a link that silently stops working —
 * `staleTime: 0` and `gcTime: 0` mean reopening the tab asks again. `enabled` keeps it from being
 * requested by a tab nobody opened: presigning is an access event, and one recorded for a tab that
 * was never looked at is a false entry in an audit trail.
 */
export function useDocumentUrl(documentId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ["evidence", "url", documentId ?? "none"] as const,
    enabled: Boolean(documentId) && enabled,
    staleTime: 0,
    gcTime: 0,
    retry: false,
    queryFn: async (): Promise<string | null> => {
      if (!documentId) return null;
      const { data, error, response } = await api.GET("/api/v1/evidence/{document_id}/url", {
        params: { path: { document_id: documentId } },
      });
      // Thrown: a refusal is not "this document has no link". `Record.tsx` reads `error` and says
      // the source could not be opened, but the hook used to succeed with `null` on every status.
      if (error) throw refusalFrom(response, error, "The link to this document could not be made.");
      return data?.url ?? null;
    },
  });
}

export type DocumentText = components["schemas"]["DocumentTextOut"];
export type DocumentTextPage = components["schemas"]["DocumentTextPageOut"];
export type ExtractedFieldSpan = components["schemas"]["ExtractedFieldSpanOut"];
/** The five answers, **taken from the generated schema** rather than restated as a union.
 *
 * The same argument as `DocumentState` at the top of this file: a status the API does not have is a
 * compile error rather than a `switch` branch that silently never runs — and this one has five arms
 * whose whole point is that they say different things.
 */
export type SpanStatus = components["schemas"]["SpanStatus"];

/** The text EmitIQ read from a document, with a re-checked position per field (ADR-0045, option B).
 *
 * **Not a viewer, and nothing here is cached as though it were a file.** The route re-reads the
 * stored object and re-checks every span before answering, which is the whole guarantee: ADR-0045
 * says a span is *"re-checked against the text before it is rendered, or it is not shown"*. So the
 * answer is about a moment, and `staleTime: 0` keeps it that way — a cached pane could draw a
 * highlight the server would no longer confirm.
 *
 * **`retry: false`** because the two interesting answers are both refusals a retry cannot improve:
 * a 404 means nobody has read this document yet, and a 422 means ADR-0028 refused it — a scan, a
 * spreadsheet, a file whose bytes have gone.
 *
 * **`null` on a 404 and a throw on everything else.** The nine read hooks that returned `data ?? []`
 * made a failed request *succeed* with nothing in it; this hook's version of that defect would be a
 * pane that rendered "no positions recorded" for a 500, which reads as a fact about the document.
 * `enabled` keeps it from being requested by a tab nobody opened — unlike `useDocumentUrl` that is
 * not because presigning is an access event, but because reading a document's bytes is work.
 */
export function useDocumentText(documentId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ["evidence", "documents", documentId ?? "none", "text"] as const,
    enabled: Boolean(documentId) && enabled,
    retry: false,
    staleTime: 0,
    queryFn: async (): Promise<DocumentText | null> => {
      if (!documentId) return null;
      const { data, error, response } = await api.GET("/api/v1/evidence/{document_id}/text", {
        params: { path: { document_id: documentId } },
      });
      // "Nobody has read this document yet" is a state to render, not a failure — the same
      // distinction `useReview` draws one route over, with the same 404 and the same code.
      if (response.status === 404) return null;
      // Everything else throws, **including the 422**, so the screen can show ADR-0028's own code
      // and remedy rather than an empty box. A `return data ?? null` here would turn a scan into a
      // pane with no pages in it, which is exactly what ADR-0045 asks not to happen.
      if (error) {
        throw refusalFrom(response, error, "The text of this document could not be read.");
      }
      return data ?? null;
    },
  });
}

/** One document's metadata, for the record page's source tab.
 *
 * `null` on a 404 rather than a thrown error: a record may cite a document that no longer exists,
 * and the page has to say *that* rather than fail. The receipt's `evidence_broken_at` is where the
 * same distinction already lives on the figure side.
 */
export function useDocument(documentId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ["evidence", "document", documentId ?? "none"] as const,
    enabled: Boolean(documentId) && enabled,
    retry: false,
    queryFn: async (): Promise<DocumentRow | null> => {
      if (!documentId) return null;
      const { data, error, response } = await api.GET("/api/v1/evidence/{document_id}", {
        params: { path: { document_id: documentId } },
      });
      // A 404 is "no such document", which a screen renders as a broken evidence chain. Anything
      // else is a failed read, and the two are opposite sentences.
      if (response.status === 404) return null;
      if (error) throw refusalFrom(response, error, "That document could not be read.");
      return data ?? null;
    },
  });
}
